import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { FormField } from '@/components/forms/FormField'
import { MultiSelectChips } from '@/components/forms/MultiSelectChips'
import { Typeahead } from '@/components/forms/Typeahead'
import { catalogService, type CatalogItem } from '@/services/catalog'
import { listCustomersApi } from '@/services/customers/apiClient'
import type { Customer } from '@/services/customers/types'
import {
  inchToMm,
  PRODUCT_STATUS_OPTIONS,
  productService,
  type ProductStatus,
} from '@/services/products'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import { pickDefaultCreateCustomer } from './defaultCreateCustomer'

export function ProductCreatePage() {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [status, setStatus] = useState<ProductStatus>('pending')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [colorIds, setColorIds] = useState<string[]>([])
  const [woodIds, setWoodIds] = useState<string[]>([])
  const [veneerIds, setVeneerIds] = useState<string[]>([])
  const [wInch, setWInch] = useState('')
  const [dInch, setDInch] = useState('')
  const [hInch, setHInch] = useState('')
  const [note, setNote] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [colors, setColors] = useState<CatalogItem[]>([])
  const [woods, setWoods] = useState<CatalogItem[]>([])
  const [veneers, setVeneers] = useState<CatalogItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void Promise.all([
      catalogService.list('color'),
      catalogService.list('wood'),
      catalogService.list('veneer'),
    ]).then(([c, wlist, v]) => {
      setColors(c)
      setWoods(wlist)
      setVeneers(v)
    })
  }, [])

  useEffect(() => {
    void listCustomersApi({ status: 'active' })
      .then((list) => {
        const active = list.filter((c) => c.status === 'active')
        setCustomers(active)
        setSelectedCustomer((prev) => prev ?? pickDefaultCreateCustomer(active))
      })
      .catch((err) => console.error('Failed to load customers', err))
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const created = await productService.create({
        code,
        name,
        customerId: selectedCustomer?.id ?? null,
        status,
        colorIds,
        woodIds,
        veneerIds,
        w: wInch ? inchToMm(parseFloat(wInch)) : 0,
        d: dInch ? inchToMm(parseFloat(dInch)) : 0,
        h: hInch ? inchToMm(parseFloat(hInch)) : 0,
        note,
        imageUrl: imageUrl || undefined,
      })
      navigate(`/products/${created.id}`)
    } catch (err) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mã hàng', to: '/products' },
          { label: 'Tạo mới' },
        ]}
      />
      <PageHeader
        title="Tạo mã hàng"
        subtitle="Identity + attributes. Documents gắn sau khi lưu."
      />

      <form
        onSubmit={handleSubmit}
        className="max-w-2xl space-y-4 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-5"
      >
        {error ? (
          <div className="rounded-[var(--radius-sm)] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Mã hàng" required>
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
              placeholder="MP175"
            />
          </FormField>
          <FormField label="Trạng thái" required>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ProductStatus)}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
            >
              {PRODUCT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Tên hàng" required>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
          />
        </FormField>

        <FormField label="Khách hàng / Thị trường">
          <Typeahead<Customer>
            items={customers}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            displayValue={(c) => `${c.code} — ${c.name}`}
            placeholder="Chọn khách hàng (không bắt buộc)..."
            clearable
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <MultiSelectChips
            label="Màu"
            options={colors}
            value={colorIds}
            onChange={setColorIds}
          />
          <MultiSelectChips
            label="Gỗ"
            options={woods}
            value={woodIds}
            onChange={setWoodIds}
          />
          <MultiSelectChips
            label="Veneer"
            options={veneers}
            value={veneerIds}
            onChange={setVeneerIds}
          />
        </div>

        <div className="rounded-[var(--radius-sm)] border border-slate-200 bg-slate-50/60 p-3">
          <div className="mb-2 text-xs font-semibold text-slate-700">
            Kích thước sản phẩm (Nhập theo Inch · Tự động quy đổi sang MM làm tròn lên)
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="W (inch)">
              <input
                type="number"
                step="0.01"
                min={0}
                value={wInch}
                onChange={(e) => setWInch(e.target.value)}
                placeholder="VD: 20"
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[var(--color-accent)]"
              />
              <div className="mt-1 text-xs font-mono font-medium text-sky-700">
                = {wInch ? inchToMm(parseFloat(wInch)) : 0} mm
              </div>
            </FormField>
            <FormField label="D (inch)">
              <input
                type="number"
                step="0.01"
                min={0}
                value={dInch}
                onChange={(e) => setDInch(e.target.value)}
                placeholder="VD: 14"
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[var(--color-accent)]"
              />
              <div className="mt-1 text-xs font-mono font-medium text-sky-700">
                = {dInch ? inchToMm(parseFloat(dInch)) : 0} mm
              </div>
            </FormField>
            <FormField label="H (inch)">
              <input
                type="number"
                step="0.01"
                min={0}
                value={hInch}
                onChange={(e) => setHInch(e.target.value)}
                placeholder="VD: 25"
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[var(--color-accent)]"
              />
              <div className="mt-1 text-xs font-mono font-medium text-sky-700">
                = {hInch ? inchToMm(parseFloat(hInch)) : 0} mm
              </div>
            </FormField>
          </div>
        </div>

        <FormField label="Ảnh (URL)">
          <input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
            placeholder="https://..."
          />
        </FormField>

        <FormField label="Ghi chú">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
          />
        </FormField>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={() => navigate('/products')}>
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Đang lưu...' : 'Tạo mã hàng'}
          </Button>
        </div>
      </form>
    </div>
  )
}
